import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, ArrowRight, RotateCcw, Trophy } from "lucide-react";
import { QUIZ_SCENARIOS } from "../../lib/constants";
import { submitScore } from "../../lib/api";
import { toast } from "sonner";

export default function QuizGame() {
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const current = QUIZ_SCENARIOS[index];

  const choose = (i) => {
    if (revealed) return;
    setPicked(i);
    setRevealed(true);
    if (current.options[i].correct) setScore((s) => s + 20);
  };

  const next = () => {
    if (index + 1 >= QUIZ_SCENARIOS.length) {
      setDone(true);
      if (!submitted) {
        submitScore("quiz", "guest", score + (current.options[picked]?.correct ? 0 : 0)).catch(
          () => {}
        );
        setSubmitted(true);
      }
    } else {
      setIndex((i) => i + 1);
      setPicked(null);
      setRevealed(false);
    }
  };

  const reset = () => {
    setIndex(0);
    setPicked(null);
    setRevealed(false);
    setScore(0);
    setDone(false);
    setSubmitted(false);
  };

  if (done) {
    const perfect = score === QUIZ_SCENARIOS.length * 20;
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-10"
        data-testid="quiz-results"
      >
        <Trophy size={56} className="mx-auto text-[#E60000] mb-3" />
        <h3 className="font-display font-black text-3xl text-[#1A1414] mb-2">
          Mission complete.
        </h3>
        <p className="text-lg text-[#1A1414]/70 mb-1">
          You scored{" "}
          <span className="text-[#E60000] font-bold">{score}</span> /{" "}
          {QUIZ_SCENARIOS.length * 20} Hero Stones.
        </p>
        <p className="text-sm text-[#1A1414]/60 mb-6 max-w-md mx-auto">
          {perfect
            ? "Flawless. You see what isn't being said. That's Mission X."
            : "Every wrong answer is just another rep. Replay and lock it in."}
        </p>
        <button
          onClick={reset}
          data-testid="quiz-replay-btn"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#8B0000] hover:-translate-y-0.5 transition-all"
        >
          <RotateCcw size={16} /> Replay
        </button>
      </motion.div>
    );
  }

  return (
    <div data-testid="quiz-game" className="w-full">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.22em] font-bold text-[#8B0000]">
          Scenario {index + 1} / {QUIZ_SCENARIOS.length}
        </span>
        <span className="text-xs font-bold text-[#1A1414]/70">
          {score} Hero Stones
        </span>
      </div>

      <div className="h-1.5 bg-[#FF8080]/15 rounded-full overflow-hidden mb-6">
        <motion.div
          animate={{ width: `${((index + (revealed ? 1 : 0)) / QUIZ_SCENARIOS.length) * 100}%` }}
          transition={{ duration: 0.5 }}
          className="h-full bg-gradient-to-r from-[#FF8080] to-[#E60000]"
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.35 }}
        >
          <h3 className="font-display font-black text-xl sm:text-2xl text-[#1A1414] mb-5 leading-snug">
            {current.scenario}
          </h3>

          <div className="grid gap-3">
            {current.options.map((opt, i) => {
              const isPicked = picked === i;
              const showCorrect = revealed && opt.correct;
              const showWrong = revealed && isPicked && !opt.correct;
              return (
                <button
                  key={i}
                  onClick={() => choose(i)}
                  disabled={revealed}
                  data-testid={`quiz-option-${i}`}
                  className={`text-left p-4 rounded-2xl border-2 font-medium transition-all ${
                    showCorrect
                      ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                      : showWrong
                      ? "border-[#E60000] bg-[#FF8080]/15 text-[#8B0000]"
                      : isPicked
                      ? "border-[#E60000] bg-white"
                      : "border-[#FF8080]/30 bg-white hover:border-[#E60000] hover:-translate-y-0.5 hover:shadow-arc"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {revealed && opt.correct && (
                      <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                    )}
                    {revealed && isPicked && !opt.correct && (
                      <XCircle size={20} className="text-[#E60000] shrink-0 mt-0.5" />
                    )}
                    <span>{opt.text}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <AnimatePresence>
            {revealed && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-5 p-4 rounded-2xl bg-[#8B0000] text-white"
                data-testid="quiz-insight"
              >
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF8080] mb-1">
                  Mission X Insight
                </p>
                <p className="text-sm leading-relaxed">{current.insight}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {revealed && (
            <div className="mt-5 flex justify-end">
              <button
                onClick={next}
                data-testid="quiz-next-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E60000] text-white font-display font-bold shadow-arc hover:bg-[#8B0000] hover:-translate-y-0.5 transition-all"
              >
                {index + 1 >= QUIZ_SCENARIOS.length ? "See score" : "Next scenario"}
                <ArrowRight size={16} />
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
